import React, { useState, useEffect } from 'react';
import { Button } from '@mui/material';
import { GetApp, Check } from '@mui/icons-material';

interface BeforeInstallPromptEvent extends Event {
  readonly platforms: string[];
  readonly userChoice: Promise<{
    outcome: 'accepted' | 'dismissed';
    platform: string;
  }>;
  prompt(): Promise<void>;
}

export const InstallButton: React.FC = () => {
  const [deferredPrompt, setDeferredPrompt] = useState<BeforeInstallPromptEvent | null>(null);
  const [isInstalled, setIsInstalled] = useState(false);
  const [showButton, setShowButton] = useState(false);
  const [showIOSInstructions, setShowIOSInstructions] = useState(false);
  const [isIOS, setIsIOS] = useState(false);

  useEffect(() => {
    // Simple check without excessive logging
    const checkIfInstalled = () => {
      if (window.matchMedia('(display-mode: standalone)').matches || (window.navigator as any).standalone === true) {
        setIsInstalled(true);
        return true;
      }
      return false;
    };

    const handleBeforeInstallPrompt = (event: any) => {
      // console.log('PWA: beforeinstallprompt event received');

      // Always prevent the default mini-infobar
      event.preventDefault();

      // Store the event for later use
      setDeferredPrompt(event);
      setShowButton(true);

      // console.log('PWA: Install prompt captured and ready');
    };

    const handleAppInstalled = () => {
      setIsInstalled(true);
      setShowButton(false);
      setDeferredPrompt(null);
    };

    // Check if iOS
    const checkIOS = /iPad|iPhone|iPod/.test(navigator.userAgent) && !(window as any).MSStream;
    setIsIOS(checkIOS);

    // For iOS, show button if not installed (iOS doesn't fire beforeinstallprompt)
    if (checkIOS && !checkIfInstalled()) {
      setShowButton(true);
    }

    checkIfInstalled();

    window.addEventListener('beforeinstallprompt', handleBeforeInstallPrompt as EventListener);
    window.addEventListener('appinstalled', handleAppInstalled);

    // Force a check after some time (dev debugging)
    // setTimeout(() => {
    //   console.log('PWA Debug: Delayed check - Install button visible?', showButton);
    //   if ('serviceWorker' in navigator) {
    //     navigator.serviceWorker.getRegistrations().then(regs => {
    //       console.log('PWA Debug: Service Worker registrations:', regs.length);
    //       regs.forEach(reg => console.log('PWA Debug: SW state:', reg.active?.state));
    //     });
    //   }
    // }, 5000);

    return () => {
      window.removeEventListener('beforeinstallprompt', handleBeforeInstallPrompt as EventListener);
      window.removeEventListener('appinstalled', handleAppInstalled);
    };
  }, []);

  const handleInstallClick = async () => {
    // For iOS, show instructions modal
    if (isIOS) {
      setShowIOSInstructions(true);
      return;
    }

    if (!deferredPrompt) {
      // console.log('PWA: No deferred prompt available');
      return;
    }

    try {
      // console.log('PWA: Triggering install prompt...');

      // Try to show the install prompt
      if (deferredPrompt.prompt) {
        await deferredPrompt.prompt();

        // Wait for the user choice
        if (deferredPrompt.userChoice) {
          const { outcome } = await deferredPrompt.userChoice;
          // console.log(`PWA: User ${outcome} the install prompt`);

          if (outcome === 'accepted') {
            setIsInstalled(true);
          }
        }
      }
    } catch (error) {
      console.error('PWA: Install prompt error:', error);
    } finally {
      // Clear the deferred prompt since it can only be used once
      setDeferredPrompt(null);
      setShowButton(false);
    }
  };

  // Don't show anything if installed or if install prompt is not available
  if (isInstalled || !showButton) {
    return null;
  }

  return (
    <>
      <Button
        variant="contained"
        startIcon={<GetApp />}
        onClick={handleInstallClick}
        sx={{
          minWidth: 120,
          textTransform: 'none',
          backgroundColor: 'var(--primary-color)',
          '&:hover': {
            backgroundColor: 'var(--secondary-color)'
          }
        }}
      >
        Install App
      </Button>

      {/* iOS Installation Instructions Modal */}
      {showIOSInstructions && (
        <>
          <div
            className="position-fixed"
            style={{
              top: 0,
              left: 0,
              width: '100vw',
              height: '100vh',
              backgroundColor: 'rgba(0, 0, 0, 0.7)',
              zIndex: 9999,
            }}
            onClick={() => setShowIOSInstructions(false)}
          />
          <div
            className="position-fixed glassContainer"
            style={{
              top: '50%',
              left: '50%',
              transform: 'translate(-50%, -50%)',
              zIndex: 10000,
              width: '90vw',
              maxWidth: '400px',
              padding: '24px',
            }}
            onClick={(e) => e.stopPropagation()}
          >
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '16px' }}>
              <h3 style={{ margin: 0 }}>Install BROODbook</h3>
              <button
                className="icon-btn"
                onClick={() => setShowIOSInstructions(false)}
                aria-label="Close"
              >
                <span className="material-icons">close</span>
              </button>
            </div>
            <p style={{ marginBottom: '16px' }}>
              To install this app on your iPhone or iPad:
            </p>
            <ol style={{ paddingLeft: '20px', lineHeight: '1.6' }}>
              <li>Tap the Share button <span className="material-icons" style={{ fontSize: '18px', verticalAlign: 'middle' }}>ios_share</span> in Safari</li>
              <li>Scroll down and tap "Add to Home Screen"</li>
              <li>Tap "Add" to confirm</li>
            </ol>
            <p style={{ marginTop: '16px', fontSize: '14px', color: 'var(--text-secondary)' }}>
              The app will appear on your home screen and work like a native app!
            </p>
          </div>
        </>
      )}
    </>
  );
};